import {useCallback, useEffect, useState} from 'react';
import {documentPictureInPicture, supportsDocumentPictureInPicture} from '../../groupCalls/services/windowPlacement';
import {runCallControl} from '../../groupCalls/components/groupCallActions';

const copyStyles = (target: Document): void => {
    [...document.styleSheets].forEach(sheet => {
        try {
            const style = target.createElement('style');
            style.textContent = [...sheet.cssRules].map(rule => rule.cssText).join('\n');
            target.head.appendChild(style);
        } catch {
            if (sheet.href) {
                const link = target.createElement('link');
                link.rel = 'stylesheet';
                link.href = sheet.href;
                target.head.appendChild(link);
            }
        }
    });
};

export interface PictureInPicture {
    readonly window: Window | null;
    readonly open?: () => void;
    readonly close: () => void;
}

export const usePictureInPicture = (size: {width: number; height: number}): PictureInPicture => {
    const [pipWindow, setPipWindow] = useState<Window | null>(null);

    useEffect(() => () => pipWindow?.close(), [pipWindow]);

    const open = useCallback(() => {
        const api = documentPictureInPicture();
        if (!api) {
            return;
        }
        void runCallControl('picture_in_picture', async () => {
            const opened = await api.requestWindow(size);
            copyStyles(opened.document);
            opened.document.body.style.margin = '0';
            opened.document.body.style.background = '#09090B';
            opened.addEventListener('pagehide', () => setPipWindow(null), {once: true});
            setPipWindow(opened);
        });
    }, [size]);

    const close = useCallback(() => {
        pipWindow?.close();
        setPipWindow(null);
    }, [pipWindow]);

    return {window: pipWindow, open: supportsDocumentPictureInPicture() ? open : undefined, close};
};
